"use client";

import { useEffect, useState } from "react";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

interface CompanyData {
  [company: string]: string;
}

interface DataEntry {
  date: string;
  revenueData: CompanyData;
  revenuePerEmployeeData: CompanyData;
}

interface CompanyColors {
  [company: string]: string;
}

interface CompanyDisplayNames {
  [company: string]: string;
}

interface ShowCompaniesState {
  [company: string]: boolean;
}

interface ShowMetricsState {
  revenue: boolean;
  revEmp: boolean;
}

interface GrowthStats {
  revenueGrowth: number;
  revEmpGrowth: number;
}

interface ProcessedDataPoint {
  date: string;
  year: number;
  formattedDate: string;
  timestamp: number;
  revenueGenpact: number | null;
  revenueCognizant: number | null;
  revenueConcentrix: number | null;
  revenueWipro: number | null;
  revenueTCS: number | null;
  revEmpGenpact: number | null;
  revEmpCognizant: number | null;
  revEmpConcentrix: number | null;
  revEmpWipro: number | null;
  revEmpTCS: number | null;
  avgRev: number | null;
  avgRevEmp: number | null;
}

interface TooltipProps {
  active?: boolean;
  payload?: TooltipPayload[];
  label?: string;
}

interface TooltipPayload {
  value: number;
  name: string;
}

interface CompanyTooltipData {
  name: string;
  color: string;
  rev: number | null;
  revEmp: number | null;
  isAverage?: boolean;
}

interface ThemeColors {
  text: string;
  mutedText: string;
  emphasizedText: string;
  bgPrimary: string;
  bgSecondary: string;
  border: string;
  gridLines: string;
  buttonBg: string;
  buttonActiveBg: string;
  tooltipBg: string;
  tooltipBorder: string;
  tooltipShadow: string;
  boxShadow: string;
}

// Company color map using full names as keys
const COMPANY_COLORS: CompanyColors = {
  Genpact: "#4299E1", // Blue
  Cognizant: "#F6AD55", // Orange
  Concentrix: "#68D391", // Green
  Wipro: "#F56565", // Red
  TCS: "#805AD5", // Purple
};

// Company display names (including TCS expanded to Tata Consulting Services)
const COMPANY_DISPLAY_NAMES: CompanyDisplayNames = {
  Genpact: "Genpact",
  Cognizant: "Cognizant",
  Concentrix: "Concentrix",
  Wipro: "Wipro",
  TCS: "Tata Consulting Services",
};

const data: DataEntry[] = [
  {
    date: "12-31-2024",
    revenueData: {
      Genpact: "4.77B",
      Cognizant: "19.74B",
      Concentrix: "",
      Wipro: "10.38B",
      TCS: "29.46B",
    },
    revenuePerEmployeeData: {
      Genpact: "38.14K",
      Cognizant: "58.60K",
      Concentrix: "",
      Wipro: "",
      TCS: "",
    },
  },
  {
    date: "11-30-2024",
    revenueData: {
      Genpact: "",
      Cognizant: "",
      Concentrix: "9.62B",
      Wipro: "",
      TCS: "",
    },
    revenuePerEmployeeData: {
      Genpact: "",
      Cognizant: "",
      Concentrix: "21.38K",
      Wipro: "",
      TCS: "",
    },
  },
  {
    date: "09-30-2024",
    revenueData: {
      Genpact: "4.66B",
      Cognizant: "19.41B",
      Concentrix: "",
      Wipro: "10.59B",
      TCS: "29.69B",
    },
    revenuePerEmployeeData: {
      Genpact: "",
      Cognizant: "",
      Concentrix: "",
      Wipro: "",
      TCS: "",
    },
  },
  {
    date: "08-31-2024",
    revenueData: {
      Genpact: "",
      Cognizant: "",
      Concentrix: "9.40B",
      Wipro: "",
      TCS: "",
    },
    revenuePerEmployeeData: {
      Genpact: "",
      Cognizant: "",
      Concentrix: "",
      Wipro: "",
      TCS: "",
    },
  },
  {
    date: "06-30-2024",
    revenueData: {
      Genpact: "4.59B",
      Cognizant: "19.27B",
      Concentrix: "",
      Wipro: "10.66B",
      TCS: "29.27B",
    },
    revenuePerEmployeeData: {
      Genpact: "",
      Cognizant: "",
      Concentrix: "",
      Wipro: "",
      TCS: "",
    },
  },
  {
    date: "05-31-2024",
    revenueData: {
      Genpact: "",
      Cognizant: "",
      Concentrix: "8.65B",
      Wipro: "",
      TCS: "",
    },
    revenuePerEmployeeData: {
      Genpact: "",
      Cognizant: "",
      Concentrix: "",
      Wipro: "",
      TCS: "",
    },
  },
  {
    date: "03-31-2024",
    revenueData: {
      Genpact: "4.52B",
      Cognizant: "19.30B",
      Concentrix: "",
      Wipro: "10.77B",
      TCS: "28.91B",
    },
    revenuePerEmployeeData: {
      Genpact: "",
      Cognizant: "",
      Concentrix: "",
      Wipro: "46.04K",
      TCS: "48.06K",
    },
  },
  {
    date: "02-29-2024",
    revenueData: {
      Genpact: "",
      Cognizant: "",
      Concentrix: "7.88B",
      Wipro: "",
      TCS: "",
    },
    revenuePerEmployeeData: {
      Genpact: "",
      Cognizant: "",
      Concentrix: "",
      Wipro: "",
      TCS: "",
    },
  },
  {
    date: "12-31-2023",
    revenueData: {
      Genpact: "4.48B",
      Cognizant: "19.35B",
      Concentrix: "",
      Wipro: "10.90B",
      TCS: "28.70B",
    },
    revenuePerEmployeeData: {
      Genpact: "34.68K",
      Cognizant: "55.66K",
      Concentrix: "",
      Wipro: "",
      TCS: "",
    },
  },
  {
    date: "11-30-2023",
    revenueData: {
      Genpact: "",
      Cognizant: "",
      Concentrix: "7.11B",
      Wipro: "",
      TCS: "",
    },
    revenuePerEmployeeData: {
      Genpact: "",
      Cognizant: "",
      Concentrix: "16.17K",
      Wipro: "",
      TCS: "",
    },
  },
  {
    date: "09-30-2023",
    revenueData: {
      Genpact: "4.43B",
      Cognizant: "19.43B",
      Concentrix: "",
      Wipro: "11.03B",
      TCS: "28.43B",
    },
    revenuePerEmployeeData: {
      Genpact: "",
      Cognizant: "",
      Concentrix: "",
      Wipro: "",
      TCS: "",
    },
  },
  {
    date: "08-31-2023",
    revenueData: {
      Genpact: "",
      Cognizant: "",
      Concentrix: "6.52B",
      Wipro: "",
      TCS: "",
    },
    revenuePerEmployeeData: {
      Genpact: "",
      Cognizant: "",
      Concentrix: "",
      Wipro: "",
      TCS: "",
    },
  },
  {
    date: "06-30-2023",
    revenueData: {
      Genpact: "4.41B",
      Cognizant: "19.39B",
      Concentrix: "",
      Wipro: "11.18B",
      TCS: "28.27B",
    },
    revenuePerEmployeeData: {
      Genpact: "",
      Cognizant: "",
      Concentrix: "",
      Wipro: "",
      TCS: "",
    },
  },
  {
    date: "05-31-2023",
    revenueData: {
      Genpact: "",
      Cognizant: "",
      Concentrix: "6.47B",
      Wipro: "",
      TCS: "",
    },
    revenuePerEmployeeData: {
      Genpact: "",
      Cognizant: "",
      Concentrix: "",
      Wipro: "",
      TCS: "",
    },
  },
  {
    date: "03-31-2023",
    revenueData: {
      Genpact: "4.39B",
      Cognizant: "19.41B",
      Concentrix: "",
      Wipro: "11.02B",
      TCS: "27.45B",
    },
    revenuePerEmployeeData: {
      Genpact: "",
      Cognizant: "",
      Concentrix: "",
      Wipro: "44.06K",
      TCS: "44.64K",
    },
  },
  {
    date: "02-28-2023",
    revenueData: {
      Genpact: "",
      Cognizant: "",
      Concentrix: "6.42B",
      Wipro: "",
      TCS: "",
    },
    revenuePerEmployeeData: {
      Genpact: "",
      Cognizant: "",
      Concentrix: "",
      Wipro: "",
      TCS: "",
    },
  },
  {
    date: "12-31-2022",
    revenueData: {
      Genpact: "4.37B",
      Cognizant: "19.43B",
      Concentrix: "",
      Wipro: "10.66B",
      TCS: "26.23B",
    },
    revenuePerEmployeeData: {
      Genpact: "36.76K",
      Cognizant: "54.68K",
      Concentrix: "",
      Wipro: "",
      TCS: "",
    },
  },
  {
    date: "11-30-2022",
    revenueData: {
      Genpact: "",
      Cognizant: "",
      Concentrix: "6.32B",
      Wipro: "",
      TCS: "",
    },
    revenuePerEmployeeData: {
      Genpact: "",
      Cognizant: "",
      Concentrix: "20.08K",
      Wipro: "",
      TCS: "",
    },
  },
  {
    date: "09-30-2022",
    revenueData: {
      Genpact: "4.34B",
      Cognizant: "19.37B",
      Concentrix: "",
      Wipro: "10.45B",
      TCS: "25.44B",
    },
    revenuePerEmployeeData: {
      Genpact: "",
      Cognizant: "",
      Concentrix: "",
      Wipro: "",
      TCS: "",
    },
  },
  {
    date: "08-31-2022",
    revenueData: {
      Genpact: "",
      Cognizant: "",
      Concentrix: "6.15B",
      Wipro: "",
      TCS: "",
    },
    revenuePerEmployeeData: {
      Genpact: "",
      Cognizant: "",
      Concentrix: "",
      Wipro: "",
      TCS: "",
    },
  },
  {
    date: "06-30-2022",
    revenueData: {
      Genpact: "4.25B",
      Cognizant: "19.25B",
      Concentrix: "",
      Wipro: "10.43B",
      TCS: "25.21B",
    },
    revenuePerEmployeeData: {
      Genpact: "",
      Cognizant: "",
      Concentrix: "",
      Wipro: "",
      TCS: "",
    },
  },
  {
    date: "05-31-2022",
    revenueData: {
      Genpact: "",
      Cognizant: "",
      Concentrix: "5.97B",
      Wipro: "",
      TCS: "",
    },
    revenuePerEmployeeData: {
      Genpact: "",
      Cognizant: "",
      Concentrix: "",
      Wipro: "",
      TCS: "",
    },
  },
  {
    date: "03-31-2022",
    revenueData: {
      Genpact: "4.14B",
      Cognizant: "18.93B",
      Concentrix: "",
      Wipro: "10.41B",
      TCS: "25.25B",
    },
    revenuePerEmployeeData: {
      Genpact: "",
      Cognizant: "",
      Concentrix: "",
      Wipro: "43.40K",
      TCS: "42.64K",
    },
  },
  {
    date: "02-28-2022",
    revenueData: {
      Genpact: "",
      Cognizant: "",
      Concentrix: "5.77B",
      Wipro: "",
      TCS: "",
    },
    revenuePerEmployeeData: {
      Genpact: "",
      Cognizant: "",
      Concentrix: "",
      Wipro: "",
      TCS: "",
    },
  },
  {
    date: "12-31-2021",
    revenueData: {
      Genpact: "4.02B",
      Cognizant: "18.51B",
      Concentrix: "",
      Wipro: "10.00B",
      TCS: "24.81B",
    },
    revenuePerEmployeeData: {
      Genpact: "36.70K",
      Cognizant: "55.98K",
      Concentrix: "",
      Wipro: "",
      TCS: "",
    },
  },
  {
    date: "11-30-2021",
    revenueData: {
      Genpact: "",
      Cognizant: "",
      Concentrix: "5.59B",
      Wipro: "",
      TCS: "",
    },
    revenuePerEmployeeData: {
      Genpact: "",
      Cognizant: "",
      Concentrix: "19.27K",
      Wipro: "",
      TCS: "",
    },
  },
  {
    date: "09-30-2021",
    revenueData: {
      Genpact: "3.90B",
      Cognizant: "17.91B",
      Concentrix: "",
      Wipro: "9.40B",
      TCS: "23.97B",
    },
    revenuePerEmployeeData: {
      Genpact: "",
      Cognizant: "",
      Concentrix: "",
      Wipro: "",
      TCS: "",
    },
  },
  {
    date: "08-31-2021",
    revenueData: {
      Genpact: "",
      Cognizant: "",
      Concentrix: "5.42B",
      Wipro: "",
      TCS: "",
    },
    revenuePerEmployeeData: {
      Genpact: "",
      Cognizant: "",
      Concentrix: "",
      Wipro: "",
      TCS: "",
    },
  },
  {
    date: "06-30-2021",
    revenueData: {
      Genpact: "3.82B",
      Cognizant: "17.41B",
      Concentrix: "",
      Wipro: "8.78B",
      TCS: "23.04B",
    },
    revenuePerEmployeeData: {
      Genpact: "",
      Cognizant: "",
      Concentrix: "",
      Wipro: "",
      TCS: "",
    },
  },
  {
    date: "05-31-2021",
    revenueData: {
      Genpact: "",
      Cognizant: "",
      Concentrix: "5.19B",
      Wipro: "",
      TCS: "",
    },
    revenuePerEmployeeData: {
      Genpact: "",
      Cognizant: "",
      Concentrix: "",
      Wipro: "",
      TCS: "",
    },
  },
  {
    date: "03-31-2021",
    revenueData: {
      Genpact: "3.73B",
      Cognizant: "16.83B",
      Concentrix: "",
      Wipro: "8.46B",
      TCS: "22.42B",
    },
    revenuePerEmployeeData: {
      Genpact: "",
      Cognizant: "",
      Concentrix: "",
      Wipro: "42.30K",
      TCS: "45.89K",
    },
  },
  {
    date: "02-28-2021",
    revenueData: {
      Genpact: "",
      Cognizant: "",
      Concentrix: "4.88B",
      Wipro: "",
      TCS: "",
    },
    revenuePerEmployeeData: {
      Genpact: "",
      Cognizant: "",
      Concentrix: "",
      Wipro: "",
      TCS: "",
    },
  },
  {
    date: "12-31-2020",
    revenueData: {
      Genpact: "3.71B",
      Cognizant: "16.65B",
      Concentrix: "",
      Wipro: "8.40B",
      TCS: "21.95B",
    },
    revenuePerEmployeeData: {
      Genpact: "38.44K",
      Cognizant: "57.52K",
      Concentrix: "",
      Wipro: "",
      TCS: "",
    },
  },
  {
    date: "11-30-2020",
    revenueData: {
      Genpact: "",
      Cognizant: "",
      Concentrix: "4.72B",
      Wipro: "",
      TCS: "",
    },
    revenuePerEmployeeData: {
      Genpact: "",
      Cognizant: "",
      Concentrix: "17.48K",
      Wipro: "",
      TCS: "",
    },
  },
  {
    date: "09-30-2020",
    revenueData: {
      Genpact: "3.70B",
      Cognizant: "16.75B",
      Concentrix: "",
      Wipro: "8.32B",
      TCS: "21.50B",
    },
    revenuePerEmployeeData: {
      Genpact: "",
      Cognizant: "",
      Concentrix: "",
      Wipro: "",
      TCS: "",
    },
  },
  {
    date: "08-31-2020",
    revenueData: {
      Genpact: "",
      Cognizant: "",
      Concentrix: "4.63B",
      Wipro: "",
      TCS: "",
    },
    revenuePerEmployeeData: {
      Genpact: "",
      Cognizant: "",
      Concentrix: "",
      Wipro: "",
      TCS: "",
    },
  },
  {
    date: "06-30-2020",
    revenueData: {
      Genpact: "3.65B",
      Cognizant: "16.76B",
      Concentrix: "",
      Wipro: "8.10B",
      TCS: "20.79B",
    },
    revenuePerEmployeeData: {
      Genpact: "",
      Cognizant: "",
      Concentrix: "",
      Wipro: "",
      TCS: "",
    },
  },
  {
    date: "05-31-2020",
    revenueData: {
      Genpact: "",
      Cognizant: "",
      Concentrix: "4.63B",
      Wipro: "",
      TCS: "",
    },
    revenuePerEmployeeData: {
      Genpact: "",
      Cognizant: "",
      Concentrix: "",
      Wipro: "",
      TCS: "",
    },
  },
  {
    date: "03-31-2020",
    revenueData: {
      Genpact: "3.63B",
      Cognizant: "16.90B",
      Concentrix: "",
      Wipro: "8.09B",
      TCS: "20.81B",
    },
    revenuePerEmployeeData: {
      Genpact: "",
      Cognizant: "",
      Concentrix: "",
      Wipro: "46.23K",
      TCS: "46.39K",
    },
  },
  {
    date: "02-29-2020",
    revenueData: {
      Genpact: "",
      Cognizant: "",
      Concentrix: "4.72B",
      Wipro: "",
      TCS: "",
    },
    revenuePerEmployeeData: {
      Genpact: "",
      Cognizant: "",
      Concentrix: "",
      Wipro: "",
      TCS: "",
    },
  },
  {
    date: "12-31-2019",
    revenueData: {
      Genpact: "3.52B",
      Cognizant: "16.78B",
      Concentrix: "",
      Wipro: "8.47B",
      TCS: "21.76B",
    },
    revenuePerEmployeeData: {
      Genpact: "36.48K",
      Cognizant: "57.38K",
      Concentrix: "",
      Wipro: "",
      TCS: "",
    },
  },
  {
    date: "11-30-2019",
    revenueData: {
      Genpact: "",
      Cognizant: "",
      Concentrix: "4.71B",
      Wipro: "",
      TCS: "",
    },
    revenuePerEmployeeData: {
      Genpact: "",
      Cognizant: "",
      Concentrix: "20.03K",
      Wipro: "",
      TCS: "",
    },
  },
  {
    date: "09-30-2019",
    revenueData: {
      Genpact: "3.42B",
      Cognizant: "16.63B",
      Concentrix: "",
      Wipro: "8.48B",
      TCS: "21.59B",
    },
    revenuePerEmployeeData: {
      Genpact: "",
      Cognizant: "",
      Concentrix: "",
      Wipro: "",
      TCS: "",
    },
  },
  {
    date: "06-30-2019",
    revenueData: {
      Genpact: "3.27B",
      Cognizant: "16.46B",
      Concentrix: "",
      Wipro: "8.60B",
      TCS: "21.81B",
    },
    revenuePerEmployeeData: {
      Genpact: "",
      Cognizant: "",
      Concentrix: "",
      Wipro: "",
      TCS: "",
    },
  },
  {
    date: "03-31-2019",
    revenueData: {
      Genpact: "3.12B",
      Cognizant: "16.32B",
      Concentrix: "",
      Wipro: "8.44B",
      TCS: "21.11B",
    },
    revenuePerEmployeeData: {
      Genpact: "",
      Cognizant: "",
      Concentrix: "",
      Wipro: "49.66K",
      TCS: "49.74K",
    },
  },
  {
    date: "12-31-2018",
    revenueData: {
      Genpact: "3.00B",
      Cognizant: "16.13B",
      Concentrix: "",
      Wipro: "8.24B",
      TCS: "20.20B",
    },
    revenuePerEmployeeData: {
      Genpact: "34.49K",
      Cognizant: "57.26K",
      Concentrix: "",
      Wipro: "",
      TCS: "",
    },
  },
  {
    date: "11-30-2018",
    revenueData: {
      Genpact: "",
      Cognizant: "",
      Concentrix: "2.46B",
      Wipro: "",
      TCS: "",
    },
    revenuePerEmployeeData: {
      Genpact: "",
      Cognizant: "",
      Concentrix: "",
      Wipro: "",
      TCS: "",
    },
  },
  {
    date: "09-30-2018",
    revenueData: {
      Genpact: "2.90B",
      Cognizant: "15.82B",
      Concentrix: "",
      Wipro: "7.72B",
      TCS: "18.49B",
    },
    revenuePerEmployeeData: {
      Genpact: "",
      Cognizant: "",
      Concentrix: "",
      Wipro: "",
      TCS: "",
    },
  },
  {
    date: "06-30-2018",
    revenueData: {
      Genpact: "2.86B",
      Cognizant: "15.51B",
      Concentrix: "",
      Wipro: "8.01B",
      TCS: "18.67B",
    },
    revenuePerEmployeeData: {
      Genpact: "",
      Cognizant: "",
      Concentrix: "",
      Wipro: "",
      TCS: "",
    },
  },
  {
    date: "03-31-2018",
    revenueData: {
      Genpact: "2.80B",
      Cognizant: "15.18B",
      Concentrix: "",
      Wipro: "8.37B",
      TCS: "18.92B",
    },
    revenuePerEmployeeData: {
      Genpact: "",
      Cognizant: "",
      Concentrix: "",
      Wipro: "51.12K",
      TCS: "47.90K",
    },
  },
  {
    date: "12-31-2017",
    revenueData: {
      Genpact: "2.74B",
      Cognizant: "14.81B",
      Concentrix: "",
      Wipro: "8.57B",
      TCS: "18.90B",
    },
    revenuePerEmployeeData: {
      Genpact: "35.09K",
      Cognizant: "56.96K",
      Concentrix: "",
      Wipro: "",
      TCS: "",
    },
  },
  {
    date: "11-30-2017",
    revenueData: {
      Genpact: "",
      Cognizant: "",
      Concentrix: "1.99B",
      Wipro: "",
      TCS: "",
    },
    revenuePerEmployeeData: {
      Genpact: "",
      Cognizant: "",
      Concentrix: "",
      Wipro: "",
      TCS: "",
    },
  },
  {
    date: "09-30-2017",
    revenueData: {
      Genpact: "2.68B",
      Cognizant: "14.44B",
      Concentrix: "",
      Wipro: "8.38B",
      TCS: "18.30B",
    },
    revenuePerEmployeeData: {
      Genpact: "",
      Cognizant: "",
      Concentrix: "",
      Wipro: "",
      TCS: "",
    },
  },
  {
    date: "06-30-2017",
    revenueData: {
      Genpact: "2.62B",
      Cognizant: "14.13B",
      Concentrix: "",
      Wipro: "8.52B",
      TCS: "18.30B",
    },
    revenuePerEmployeeData: {
      Genpact: "",
      Cognizant: "",
      Concentrix: "",
      Wipro: "",
      TCS: "",
    },
  },
  {
    date: "03-31-2017",
    revenueData: {
      Genpact: "2.58B",
      Cognizant: "13.83B",
      Concentrix: "",
      Wipro: "8.49B",
      TCS: "18.20B",
    },
    revenuePerEmployeeData: {
      Genpact: "",
      Cognizant: "",
      Concentrix: "",
      Wipro: "53.08K",
      TCS: "47.01K",
    },
  },
  {
    date: "12-31-2016",
    revenueData: {
      Genpact: "2.57B",
      Cognizant: "13.49B",
      Concentrix: "",
      Wipro: "8.05B",
      TCS: "17.18B",
    },
    revenuePerEmployeeData: {
      Genpact: "34.28K",
      Cognizant: "51.83K",
      Concentrix: "",
      Wipro: "",
      TCS: "",
    },
  },
  {
    date: "09-30-2016",
    revenueData: {
      Genpact: "2.54B",
      Cognizant: "13.26B",
      Concentrix: "",
      Wipro: "8.08B",
      TCS: "17.17B",
    },
    revenuePerEmployeeData: {
      Genpact: "",
      Cognizant: "",
      Concentrix: "",
      Wipro: "",
      TCS: "",
    },
  },
  {
    date: "06-30-2016",
    revenueData: {
      Genpact: "2.50B",
      Cognizant: "12.99B",
      Concentrix: "",
      Wipro: "7.79B",
      TCS: "16.62B",
    },
    revenuePerEmployeeData: {
      Genpact: "",
      Cognizant: "",
      Concentrix: "",
      Wipro: "",
      TCS: "",
    },
  },
  {
    date: "03-31-2016",
    revenueData: {
      Genpact: "2.48B",
      Cognizant: "12.71B",
      Concentrix: "",
      Wipro: "7.73B",
      TCS: "16.39B",
    },
    revenuePerEmployeeData: {
      Genpact: "",
      Cognizant: "",
      Concentrix: "",
      Wipro: "51.55K",
      TCS: "46.33K",
    },
  },
  {
    date: "12-31-2015",
    revenueData: {
      Genpact: "2.46B",
      Cognizant: "12.42B",
      Concentrix: "",
      Wipro: "7.51B",
      TCS: "15.77B",
    },
    revenuePerEmployeeData: {
      Genpact: "34.18K",
      Cognizant: "56.00K",
      Concentrix: "",
      Wipro: "",
      TCS: "",
    },
  },
  {
    date: "09-30-2015",
    revenueData: {
      Genpact: "2.42B",
      Cognizant: "11.93B",
      Concentrix: "",
      Wipro: "7.45B",
      TCS: "15.49B",
    },
    revenuePerEmployeeData: {
      Genpact: "",
      Cognizant: "",
      Concentrix: "",
      Wipro: "",
      TCS: "",
    },
  },
  {
    date: "06-30-2015",
    revenueData: {
      Genpact: "2.39B",
      Cognizant: "11.32B",
      Concentrix: "",
      Wipro: "7.55B",
      TCS: "15.42B",
    },
    revenuePerEmployeeData: {
      Genpact: "",
      Cognizant: "",
      Concentrix: "",
      Wipro: "",
      TCS: "",
    },
  },
  {
    date: "03-31-2015",
    revenueData: {
      Genpact: "2.34B",
      Cognizant: "10.75B",
      Concentrix: "",
      Wipro: "7.51B",
      TCS: "15.14B",
    },
    revenuePerEmployeeData: {
      Genpact: "",
      Cognizant: "",
      Concentrix: "",
      Wipro: "47.47K",
      TCS: "47.36K",
    },
  },
];

const RevenueAnalysisChart = (): JSX.Element => {
  // State for chart data
  const [chartData, setChartData] = useState<ProcessedDataPoint[]>([]);
  // State for company visibility toggles
  const [showCompanies, setShowCompanies] = useState<ShowCompaniesState>({
    Genpact: true,
    Cognizant: true,
    Concentrix: true,
    Wipro: true,
    TCS: true,
  });

  // State for metric visibility toggles
  const [showMetrics, setShowMetrics] = useState<ShowMetricsState>({
    revenue: true,
    revEmp: true,
  });

  // State for loading and error handling
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // State for growth statistics
  const [growthStats, setGrowthStats] = useState<GrowthStats>({
    revenueGrowth: 0,
    revEmpGrowth: 0,
  });

  // State for view mode (individual companies or averages only)
  const [showAveragesOnly, setShowAveragesOnly] = useState<boolean>(true);

  // State for dark mode detection
  const [isDarkMode, setIsDarkMode] = useState<boolean>(false);

  // Detect dark mode
  useEffect(() => {
    // Check for system preference
    const darkModeQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handleChange = (e: MediaQueryListEvent): void => {
      setIsDarkMode(e.matches);
    };

    // Set initial mode
    setIsDarkMode(
      darkModeQuery.matches ||
        document.documentElement.classList.contains("dark")
    );

    // Listen for changes
    darkModeQuery.addEventListener("change", handleChange);

    // Check for class-based dark mode (most Tailwind implementations)
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.attributeName === "class") {
          setIsDarkMode(document.documentElement.classList.contains("dark"));
        }
      }
    });

    observer.observe(document.documentElement, { attributes: true });

    return () => {
      darkModeQuery.removeEventListener("change", handleChange);
      observer.disconnect();
    };
  }, []);

  // Theme colors based on mode
  const theme: ThemeColors = {
    text: isDarkMode ? "#e2e8f0" : "#4a5568",
    mutedText: isDarkMode ? "#a0aec0" : "#718096",
    emphasizedText: isDarkMode ? "#f7fafc" : "#2d3748",
    bgPrimary: isDarkMode ? "#1a202c" : "white",
    bgSecondary: isDarkMode ? "#2d3748" : "#f7fafc",
    border: isDarkMode ? "#4a5568" : "#e2e8f0",
    gridLines: isDarkMode
      ? "rgba(160, 174, 192, 0.2)"
      : "rgba(160, 174, 192, 0.4)",
    buttonBg: isDarkMode ? "#2d3748" : "#f0f4f8",
    buttonActiveBg: isDarkMode ? "#4a5568" : "#e2e8f0",
    tooltipBg: isDarkMode ? "#2d3748" : "white",
    tooltipBorder: isDarkMode ? "#4a5568" : "#ccc",
    tooltipShadow: isDarkMode ? "rgba(35, 31, 32, 0.3)" : "rgba(35, 31, 32, 0.1)",
    boxShadow: isDarkMode ? "rgba(35, 31, 32, 0.3)" : "rgba(35, 31, 32, 0.1)",
  };

  // Helper function to convert values like "4.77B" to numbers
  const parseValueToNumber = (value: string): number | null => {
    if (!value) return null;

    const multipliers: Record<string, number> = {
      K: 1000,
      M: 1000000,
      B: 1000000000,
      T: 1000000000000,
    };

    const match = value.match(/^([\d.]+)([KMBT])/);
    if (!match) return parseFloat(value);

    const number = parseFloat(match[1]);
    const multiplier = multipliers[match[2]];

    return number * multiplier;
  };

  // Format number to display (B for billions, M for millions, K for thousands)
  const formatNumber = (number: number, type: "revenue" | "revEmp"): string => {
    if (number === null || number === undefined) return "-";

    if (type === "revenue") {
      return `${(number / 1000000000).toFixed(2)}B`;
    } else if (type === "revEmp") {
      return `${(number / 1000).toFixed(1)}K`;
    }

    return number.toString();
  };

  useEffect(() => {
    const processData = async (): Promise<void> => {
      try {
        setIsLoading(true);

        console.log("Starting data processing from cleaned JSON data");

        // Track peak TTM values for each company and metric by fiscal year
        const peakTtmValues: Record<number, Partial<ProcessedDataPoint>> = {};

        // Helper to extract date and year
        const extractDateInfo = (
          dateStr: string
        ): { date: Date; year: number } | null => {
          try {
            // Extract components from date strings like "12-31-2024"
            const match = dateStr.match(/(\d{2})-(\d{2})-(\d{4})/);
            if (match) {
              const [, month, day, year] = match;
              return {
                date: new Date(`${year}-${month}-${day}`),
                year: parseInt(year),
              };
            }
            return null;
          } catch (err) {
            console.error(`Failed to parse date: ${dateStr}`, err);
            return null;
          }
        };

        // Process each entry in the data array
        for (const entry of data) {
          // Extract and validate date
          const dateInfo = extractDateInfo(entry.date);
          if (!dateInfo) {
            console.warn(`Skipping entry with invalid date: ${entry.date}`);
            continue;
          }

          const { year } = dateInfo;

          // Skip data outside our range
          if (year < 2015 || year > 2024) {
            console.warn(`Skipping entry outside of year range: ${year}`);
            continue;
          }

          // Initialize year data if needed
          if (!peakTtmValues[year]) {
            peakTtmValues[year] = {
              date: `${year}-12-31`, // Use end of year as display date
              year,
              // Revenue fields
              revenueGenpact: null,
              revenueCognizant: null,
              revenueConcentrix: null,
              revenueWipro: null,
              revenueTCS: null,
              // Revenue per employee fields
              revEmpGenpact: null,
              revEmpCognizant: null,
              revEmpConcentrix: null,
              revEmpWipro: null,
              revEmpTCS: null,
            };
          }

          // Helper to update max value for a specific metric
          const updateMaxValue = (
            dataObj: CompanyData,
            prefix: string,
            company: string
          ): void => {
            if (!dataObj || !dataObj[company] || !dataObj[company].trim())
              return;

            const value = parseValueToNumber(dataObj[company]);
            if (value === null || isNaN(value)) return;

            const metricKey = `${prefix}${company}` as keyof ProcessedDataPoint;

            // Update if the current value is null or the new value is higher
            if (
              peakTtmValues[year][metricKey] === null ||
              (typeof peakTtmValues[year][metricKey] === "number" &&
                value > (peakTtmValues[year][metricKey] as number))
            ) {
              // Use type assertion with unknown as intermediate step
              (peakTtmValues[year] as unknown as Record<string, number | null>)[
                metricKey
              ] = value;
              console.log(`Updated ${year} ${metricKey}: ${value}`);
            }
          };

          // Process revenue data for each company
          if (entry.revenueData) {
            const companies = Object.keys(entry.revenueData);
            for (const company of companies) {
              updateMaxValue(entry.revenueData, "revenue", company);
            }
          }

          // Process revenue per employee data for each company
          if (entry.revenuePerEmployeeData) {
            const companies = Object.keys(entry.revenuePerEmployeeData);
            for (const company of companies) {
              updateMaxValue(entry.revenuePerEmployeeData, "revEmp", company);
            }
          }
        }

        // Convert to array and sort by year
        const peakTtmData = Object.values(peakTtmValues).sort(
          (a, b) => (a.year || 0) - (b.year || 0)
        );

        console.log(`Processed peak TTM data for ${peakTtmData.length} years`);

        // Check if we have enough data
        if (peakTtmData.length === 0) {
          throw new Error(
            "No valid peak TTM data could be extracted from the data"
          );
        }

        // Process the data for chart display
        const processedData: ProcessedDataPoint[] = peakTtmData.map((item) => {
          // Process date
          const date = new Date(item.date || "");

          // Calculate averages for revenue data
          const revValues = [
            item.revenueGenpact,
            item.revenueCognizant,
            item.revenueConcentrix,
            item.revenueWipro,
            item.revenueTCS,
          ].filter((val): val is number => val !== null && val !== undefined);

          // Calculate averages for revenue per employee data
          const revEmpValues = [
            item.revEmpGenpact,
            item.revEmpCognizant,
            item.revEmpConcentrix,
            item.revEmpWipro,
            item.revEmpTCS,
          ].filter((val): val is number => val !== null && val !== undefined);

          return {
            ...(item as ProcessedDataPoint),
            formattedDate: (item.date || "").substring(0, 7), // YYYY-MM format
            timestamp: date.getTime(),
            avgRev:
              revValues.length > 0
                ? revValues.reduce((sum, val) => sum + val, 0) /
                  revValues.length
                : null,
            avgRevEmp:
              revEmpValues.length > 0
                ? revEmpValues.reduce((sum, val) => sum + val, 0) /
                  revEmpValues.length
                : null,
          };
        });

        // Test the sorting
        processedData.sort((a, b) => a.timestamp - b.timestamp);

        // Calculate growth rates from oldest to newest
        const firstPoint = processedData[0];
        const lastPoint = processedData[processedData.length - 1];

        const revenueGrowth =
          (((lastPoint.avgRev || 0) - (firstPoint.avgRev || 0)) /
            (firstPoint.avgRev || 1)) *
          100;
        const revEmpGrowth =
          (((lastPoint.avgRevEmp || 0) - (firstPoint.avgRevEmp || 0)) /
            (firstPoint.avgRevEmp || 1)) *
          100;

        setGrowthStats({
          revenueGrowth: parseFloat(revenueGrowth.toFixed(2)),
          revEmpGrowth: parseFloat(revEmpGrowth.toFixed(2)),
        });

        console.log("Processed data:", processedData);
        console.log(
          `Growth rates: Revenue=${revenueGrowth.toFixed(
            2
          )}%, Rev/Emp=${revEmpGrowth.toFixed(2)}%`
        );

        setChartData(processedData);
        setIsLoading(false);
      } catch (err) {
        console.error("Error processing data:", err);
        setError(
          `Failed to process data: ${
            err instanceof Error ? err.message : "Unknown error"
          }`
        );
        setIsLoading(false);
      }
    };

    processData();
  }, []);

  // Toggle company visibility
  const toggleCompany = (company: string): void => {
    setShowCompanies((prev) => ({
      ...prev,
      [company]: !prev[company],
    }));
  };

  // Toggle metric visibility
  const toggleMetric = (metric: keyof ShowMetricsState): void => {
    setShowMetrics((prev) => ({
      ...prev,
      [metric]: !prev[metric],
    }));
  };

  // Custom tooltip for the dual-axis chart showing all values for the selected x value
  const CustomTooltip = ({
    active,
    payload,
    label,
  }: TooltipProps): JSX.Element | null => {
    if (active && payload && payload.length) {
      // Get a reference to the data point for this x value
      const dataPoint = chartData.find((d) => d.formattedDate === label);

      // Organize by company
      const companies: Record<string, CompanyTooltipData> = {};

      if (dataPoint) {
        // Add companies with their respective values
        if (dataPoint.revenueGenpact !== null) {
          companies["Genpact"] = {
            name: COMPANY_DISPLAY_NAMES["Genpact"],
            color: COMPANY_COLORS["Genpact"],
            rev: dataPoint.revenueGenpact,
            revEmp: dataPoint.revEmpGenpact,
          };
        }

        if (dataPoint.revenueCognizant !== null) {
          companies["Cognizant"] = {
            name: COMPANY_DISPLAY_NAMES["Cognizant"],
            color: COMPANY_COLORS["Cognizant"],
            rev: dataPoint.revenueCognizant,
            revEmp: dataPoint.revEmpCognizant,
          };
        }

        if (dataPoint.revenueConcentrix !== null) {
          companies["Concentrix"] = {
            name: COMPANY_DISPLAY_NAMES["Concentrix"],
            color: COMPANY_COLORS["Concentrix"],
            rev: dataPoint.revenueConcentrix,
            revEmp: dataPoint.revEmpConcentrix,
          };
        }

        if (dataPoint.revenueWipro !== null) {
          companies["Wipro"] = {
            name: COMPANY_DISPLAY_NAMES["Wipro"],
            color: COMPANY_COLORS["Wipro"],
            rev: dataPoint.revenueWipro,
            revEmp: dataPoint.revEmpWipro,
          };
        }

        if (dataPoint.revenueTCS !== null) {
          companies["TCS"] = {
            name: COMPANY_DISPLAY_NAMES["TCS"],
            color: COMPANY_COLORS["TCS"],
            rev: dataPoint.revenueTCS,
            revEmp: dataPoint.revEmpTCS,
          };
        }

        // Add averages
        companies["Avg"] = {
          name: "Average",
          color: isDarkMode ? "#ffffff" : "#000000",
          rev: dataPoint.avgRev,
          revEmp: dataPoint.avgRevEmp,
          isAverage: true,
        };
      }

      return (
        <div
          style={{
            backgroundColor: theme.tooltipBg,
            padding: "12px",
            border: `1px solid ${theme.tooltipBorder}`,
            borderRadius: "4px",
            boxShadow: `0 2px 4px ${theme.tooltipShadow}`,
            maxWidth: "300px",
            color: theme.text,
          }}
        >
          <p
            style={{
              fontWeight: "bold",
              margin: "0 0 8px 0",
              borderBottom: `1px solid ${theme.border}`,
              paddingBottom: "4px",
            }}
          >
            {label}
          </p>

          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              fontSize: "0.9rem",
            }}
          >
            <thead>
              <tr>
                <th
                  style={{
                    textAlign: "left",
                    paddingBottom: "4px",
                    color: theme.emphasizedText,
                  }}
                >
                  Company
                </th>
                <th
                  style={{
                    textAlign: "left",
                    paddingBottom: "4px",
                    paddingLeft: "6px",
                    color: theme.emphasizedText,
                  }}
                >
                  Revenue
                </th>
                <th
                  style={{
                    textAlign: "right",
                    paddingBottom: "4px",
                    paddingLeft: "10px",
                    color: theme.emphasizedText,
                  }}
                >
                  Rev/Emp
                </th>
              </tr>
            </thead>
            <tbody>
              {Object.values(companies).map((company, index) => (
                <tr
                  key={index}
                  style={{
                    fontWeight: company.isAverage ? "bold" : "normal",
                    backgroundColor: company.isAverage
                      ? isDarkMode
                        ? "#374151"
                        : "#f8f9fa"
                      : "transparent",
                  }}
                >
                  <td
                    style={{
                      color: company.color,
                      paddingRight: "8px",
                      paddingTop: "4px",
                      paddingBottom: company.isAverage ? "4px" : "2px",
                      borderTop: company.isAverage
                        ? `1px solid ${theme.border}`
                        : "none",
                    }}
                  >
                    {company.name}
                  </td>
                  <td
                    style={{
                      textAlign: "right",
                      paddingRight: "8px",
                      paddingTop: "4px",
                      paddingBottom: company.isAverage ? "4px" : "2px",
                      borderTop: company.isAverage
                        ? `1px solid ${theme.border}`
                        : "none",
                      color: theme.text,
                    }}
                  >
                    {company.rev ? formatNumber(company.rev, "revenue") : "-"}
                  </td>
                  <td
                    style={{
                      textAlign: "right",
                      paddingTop: "4px",
                      paddingBottom: company.isAverage ? "4px" : "2px",
                      borderTop: company.isAverage
                        ? `1px solid ${theme.border}`
                        : "none",
                      color: theme.text,
                    }}
                  >
                    {company.revEmp
                      ? formatNumber(company.revEmp, "revEmp")
                      : "-"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }
    return null;
  };

  // Show loading state
  if (isLoading) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "400px",
          color: theme.text,
        }}
      >
        <p>Loading revenue data...</p>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          padding: "20px",
          color: "red",
        }}
      >
        <p>{error}</p>

        <h3>CSV Data Analysis</h3>
        <div
          style={{
            maxHeight: "400px",
            overflowY: "scroll",
            width: "100%",
            border: `1px solid ${theme.border}`,
            padding: "10px",
            marginTop: "20px",
            backgroundColor: theme.bgSecondary,
            color: theme.text,
          }}
        >
          <p>Error occurred while processing the BPO industry data.</p>
          <p>Data points available: {data.length}</p>

          <h4>First data point:</h4>
          <pre style={{ whiteSpace: "pre-wrap", fontSize: "12px" }}>
            {JSON.stringify(data[0], null, 2)}
          </pre>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: "20px", color: theme.text }}>
      <div>
        {/* Controls row */}
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            flexWrap: "wrap",
            gap: "16px",
            marginBottom: "10px",
          }}
        >
          {/* View mode toggle */}
          <div style={{ display: "flex", alignItems: "center" }}>
            <button
              onClick={() => setShowAveragesOnly(!showAveragesOnly)}
              style={{
                backgroundColor: showAveragesOnly
                  ? theme.buttonActiveBg
                  : theme.buttonBg,
                border: `1px solid ${theme.border}`,
                borderRadius: "3px",
                padding: "4px 8px",
                fontSize: "0.75rem",
                cursor: "pointer",
                fontWeight: showAveragesOnly ? "600" : "normal",
                color: theme.text,
              }}
            >
              {showAveragesOnly ? "Show Selected Companies" : "Show Averages"}
            </button>
          </div>

          {/* Metric toggles */}
          <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
            <span style={{ fontSize: "0.75rem", color: theme.mutedText }}>
              Metrics:
            </span>
            <button
              onClick={() => toggleMetric("revenue")}
              style={{
                backgroundColor: showMetrics.revenue
                  ? theme.buttonActiveBg
                  : theme.buttonBg,
                border: `1px solid ${theme.border}`,
                borderRadius: "3px",
                padding: "4px 8px",
                fontSize: "0.75rem",
                cursor: "pointer",
                color: theme.text,
              }}
            >
              Revenue TTM
            </button>
            <button
              onClick={() => toggleMetric("revEmp")}
              style={{
                backgroundColor: showMetrics.revEmp
                  ? theme.buttonActiveBg
                  : theme.buttonBg,
                border: `1px solid ${theme.border}`,
                borderRadius: "3px",
                padding: "4px 8px",
                fontSize: "0.75rem",
                cursor: "pointer",
                color: theme.text,
              }}
            >
              Revenue/Employee TTM
            </button>
          </div>

          {/* Company toggles */}
          {!showAveragesOnly && (
            <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
              <span style={{ fontSize: "0.75rem", color: theme.mutedText }}>
                Companies:
              </span>
              {Object.keys(COMPANY_DISPLAY_NAMES).map((company) => (
                <button
                  key={company}
                  onClick={() => toggleCompany(company)}
                  style={{
                    backgroundColor: showCompanies[company]
                      ? theme.buttonActiveBg
                      : theme.buttonBg,
                    border: `1px solid ${theme.border}`,
                    borderRadius: "3px",
                    padding: "4px 8px",
                    fontSize: "0.75rem",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    color: theme.text,
                  }}
                >
                  <span
                    style={{
                      color: COMPANY_COLORS[company],
                      fontSize: "12px",
                      marginRight: "4px",
                    }}
                  >
                    ●
                  </span>
                  {COMPANY_DISPLAY_NAMES[company]}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Stats summary */}
        <div
          style={{
            display: "flex",
            gap: "24px",
            color: theme.text,
            fontSize: "0.875rem",
            marginTop: "10px",
            backgroundColor: theme.bgSecondary,
            padding: "10px",
            borderRadius: "6px",
            border: `1px solid ${theme.border}`,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <span>Total Revenue Growth:</span>
            <span
              style={{
                fontWeight: "600",
                display: "flex",
                alignItems: "center",
                color: "#2f855a",
              }}
            >
              +{growthStats.revenueGrowth}% ↗
            </span>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <span>Rev/Employee Growth:</span>
            <span
              style={{
                fontWeight: "600",
                display: "flex",
                alignItems: "center",
                color: growthStats.revEmpGrowth > 0 ? "#2f855a" : "#d69e2e",
              }}
            >
              {growthStats.revEmpGrowth > 0 ? "+" : ""}
              {growthStats.revEmpGrowth}%
              {growthStats.revEmpGrowth > 0 ? " ↗" : " ↘"}
            </span>
          </div>
        </div>
      </div>

      {/* Combined chart */}
      <div style={{ height: "500px", marginBottom: "16px" }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={chartData} // Already sorted in the processData function
            margin={{ top: 20, right: 30, left: 20, bottom: 10 }}
          >
            <CartesianGrid
              strokeDasharray="3 3"
              opacity={0.4}
              stroke={theme.gridLines}
            />
            <XAxis
              dataKey="formattedDate"
              tick={{ fontSize: 12, fill: theme.text }}
              tickFormatter={(tick) => tick}
              stroke={theme.mutedText}
            />

            {/* Left Y-axis for revenue */}
            <YAxis
              yAxisId="left"
              tick={{ fontSize: 12, fill: theme.text }}
              tickFormatter={(tick) => `${(tick / 1000000000).toFixed(0)}B`}
              domain={["auto", "auto"]}
              stroke={theme.mutedText}
              label={{
                value: "Revenue TTM (Billions USD)",
                angle: -90,
                position: "insideLeft",
                style: {
                  textAnchor: "middle",
                  fill: theme.mutedText,
                  fontSize: 12,
                },
              }}
            />

            {/* Right Y-axis for revenue per employee */}
            <YAxis
              yAxisId="right"
              orientation="right"
              tick={{ fontSize: 12, fill: theme.text }}
              tickFormatter={(tick) => `${(tick / 1000).toFixed(0)}K`}
              domain={[0, 100000]}
              stroke={theme.mutedText}
              label={{
                value: "Revenue per Employee TTM (Thousands USD)",
                angle: 90,
                position: "insideRight",
                style: {
                  textAnchor: "middle",
                  fill: theme.mutedText,
                  fontSize: 12,
                },
              }}
            />

            <Tooltip content={<CustomTooltip />} />
            <Legend
              wrapperStyle={{
                fontSize: "0.7rem",
                marginTop: "5px",
                lineHeight: "0.9em",
                color: theme.text,
              }}
              iconSize={10}
              layout="horizontal"
              verticalAlign="bottom"
              align="center"
            />

            {/* Average Revenue line - visible when revenue metric is enabled */}
            {showMetrics.revenue && (
              <Line
                yAxisId="left"
                type="monotone"
                dataKey="avgRev"
                name="Avg Revenue"
                stroke={isDarkMode ? "#ffffff" : "#000000"}
                strokeWidth={3}
                dot={false}
                connectNulls={true}
              />
            )}

            {/* Average Revenue per Employee line - visible when revEmp metric is enabled */}
            {showMetrics.revEmp && (
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="avgRevEmp"
                name="Avg Rev/Emp"
                stroke={isDarkMode ? "#a0aec0" : "#666666"}
                strokeWidth={3}
                strokeDasharray="6 3"
                dot={false}
                connectNulls={true}
              />
            )}

            {/* Company Revenue lines */}
            {showMetrics.revenue &&
              !showAveragesOnly &&
              showCompanies["Genpact"] && (
                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="revenueGenpact"
                  name={`${COMPANY_DISPLAY_NAMES["Genpact"]} Revenue TTM`}
                  stroke={COMPANY_COLORS["Genpact"]}
                  strokeWidth={1.5}
                  dot={{
                    r: 3,
                    stroke: COMPANY_COLORS["Genpact"],
                    fill: "white",
                  }}
                  activeDot={{ r: 5 }}
                  connectNulls={true}
                />
              )}

            {showMetrics.revenue &&
              !showAveragesOnly &&
              showCompanies["Cognizant"] && (
                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="revenueCognizant"
                  name={`${COMPANY_DISPLAY_NAMES["Cognizant"]} Revenue TTM`}
                  stroke={COMPANY_COLORS["Cognizant"]}
                  strokeWidth={1.5}
                  dot={{
                    r: 3,
                    stroke: COMPANY_COLORS["Cognizant"],
                    fill: "white",
                  }}
                  activeDot={{ r: 5 }}
                  connectNulls={true}
                />
              )}

            {showMetrics.revenue &&
              !showAveragesOnly &&
              showCompanies["Concentrix"] && (
                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="revenueConcentrix"
                  name={`${COMPANY_DISPLAY_NAMES["Concentrix"]} Revenue TTM`}
                  stroke={COMPANY_COLORS["Concentrix"]}
                  strokeWidth={1.5}
                  dot={{
                    r: 3,
                    stroke: COMPANY_COLORS["Concentrix"],
                    fill: "white",
                  }}
                  activeDot={{ r: 5 }}
                  connectNulls={true}
                />
              )}

            {showMetrics.revenue &&
              !showAveragesOnly &&
              showCompanies["Wipro"] && (
                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="revenueWipro"
                  name={`${COMPANY_DISPLAY_NAMES["Wipro"]} Revenue TTM`}
                  stroke={COMPANY_COLORS["Wipro"]}
                  strokeWidth={1.5}
                  dot={{ r: 3, stroke: COMPANY_COLORS["Wipro"], fill: "white" }}
                  activeDot={{ r: 5 }}
                  connectNulls={true}
                />
              )}

            {showMetrics.revenue &&
              !showAveragesOnly &&
              showCompanies["TCS"] && (
                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="revenueTCS"
                  name={`${COMPANY_DISPLAY_NAMES["TCS"]} Revenue TTM`}
                  stroke={COMPANY_COLORS["TCS"]}
                  strokeWidth={1.5}
                  dot={{ r: 3, stroke: COMPANY_COLORS["TCS"], fill: "white" }}
                  activeDot={{ r: 5 }}
                  connectNulls={true}
                />
              )}

            {/* Company Revenue per Employee lines - dashed pattern */}
            {showMetrics.revEmp &&
              !showAveragesOnly &&
              showCompanies["Genpact"] && (
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="revEmpGenpact"
                  name={`${COMPANY_DISPLAY_NAMES["Genpact"]} Rev/Emp TTM`}
                  stroke={COMPANY_COLORS["Genpact"]}
                  strokeWidth={1.5}
                  strokeDasharray="3 3"
                  dot={{
                    r: 3,
                    stroke: COMPANY_COLORS["Genpact"],
                    fill: COMPANY_COLORS["Genpact"],
                  }}
                  activeDot={{ r: 5 }}
                  connectNulls={true}
                />
              )}

            {showMetrics.revEmp &&
              !showAveragesOnly &&
              showCompanies["Cognizant"] && (
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="revEmpCognizant"
                  name={`${COMPANY_DISPLAY_NAMES["Cognizant"]} Rev/Emp TTM`}
                  stroke={COMPANY_COLORS["Cognizant"]}
                  strokeWidth={1.5}
                  strokeDasharray="3 3"
                  dot={{
                    r: 3,
                    stroke: COMPANY_COLORS["Cognizant"],
                    fill: COMPANY_COLORS["Cognizant"],
                  }}
                  activeDot={{ r: 5 }}
                  connectNulls={true}
                />
              )}

            {showMetrics.revEmp &&
              !showAveragesOnly &&
              showCompanies["Concentrix"] && (
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="revEmpConcentrix"
                  name={`${COMPANY_DISPLAY_NAMES["Concentrix"]} Rev/Emp TTM`}
                  stroke={COMPANY_COLORS["Concentrix"]}
                  strokeWidth={1.5}
                  strokeDasharray="3 3"
                  dot={{
                    r: 3,
                    stroke: COMPANY_COLORS["Concentrix"],
                    fill: COMPANY_COLORS["Concentrix"],
                  }}
                  activeDot={{ r: 5 }}
                  connectNulls={true}
                />
              )}

            {showMetrics.revEmp &&
              !showAveragesOnly &&
              showCompanies["Wipro"] && (
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="revEmpWipro"
                  name={`${COMPANY_DISPLAY_NAMES["Wipro"]} Rev/Emp TTM`}
                  stroke={COMPANY_COLORS["Wipro"]}
                  strokeWidth={1.5}
                  strokeDasharray="3 3"
                  dot={{
                    r: 3,
                    stroke: COMPANY_COLORS["Wipro"],
                    fill: COMPANY_COLORS["Wipro"],
                  }}
                  activeDot={{ r: 5 }}
                  connectNulls={true}
                />
              )}

            {showMetrics.revEmp &&
              !showAveragesOnly &&
              showCompanies["TCS"] && (
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="revEmpTCS"
                  name={`${COMPANY_DISPLAY_NAMES["TCS"]} Rev/Emp TTM`}
                  stroke={COMPANY_COLORS["TCS"]}
                  strokeWidth={1.5}
                  strokeDasharray="3 3"
                  dot={{
                    r: 3,
                    stroke: COMPANY_COLORS["TCS"],
                    fill: COMPANY_COLORS["TCS"],
                  }}
                  activeDot={{ r: 5 }}
                  connectNulls={true}
                />
              )}
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Chart explanation */}
      <div
        style={{
          backgroundColor: theme.bgSecondary,
          padding: "16px",
          borderRadius: "8px",
          border: `1px solid ${theme.border}`,
          fontSize: "0.95rem",
          color: theme.text,
        }}
      >
        <h3
          style={{
            fontSize: "1.1rem",
            marginTop: 0,
            marginBottom: "8px",
            color: theme.emphasizedText,
          }}
        >
          Key Insights
        </h3>
        <ul style={{ marginTop: 0, paddingLeft: "20px" }}>
          <li style={{ marginBottom: "6px" }}>
            <strong>Revenue Growth:</strong> BPO industry revenue has grown
            steadily over the 10-year period.
          </li>
          <li style={{ marginBottom: "6px" }}>
            <strong>Productivity Challenge:</strong> Despite strong revenue
            growth, revenue per employee has been mostly flat, indicating that
            headcount growth is tracking revenue growth.
          </li>
          <li style={{ marginBottom: "6px" }}>
            <strong>Efficiency Gap:</strong> The widening gap between the solid
            lines (revenue) and dashed lines (rev/employee) illustrates the
            industry&apos;s struggle to improve operational efficiency as they
            scale.
          </li>
          <li>
            <strong>AI Opportunity:</strong> This efficiency gap represents a
            significant opportunity for AI and automation to transform
            back-office operations.
          </li>
        </ul>

        <div
          style={{
            marginTop: "15px",
            fontSize: "0.75rem",
            textAlign: "right",
            color: theme.mutedText,
          }}
        >
          Data source:{" "}
          <a
            href="https://app.koyfin.com/share/99e5428eaa"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: "#4299e1" }}
          >
            Koyfin
          </a>
        </div>
      </div>
    </div>
  );
};

export { RevenueAnalysisChart };
